import { useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Group, Loader, Notification, SimpleGrid, Stack, useMantineTheme } from '@mantine/core';
import { useDocumentVisibility } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { RevisionGetResponse } from 'api/actions/revision-get/revision-get-response';
import { useApi } from 'api/api-context';
import RevisionStatusIcon from 'components/RevisionStatusIcon';
import Toast from 'components/Toast';
import FormInputGroup from 'components/forms/FormInputGroup';
import RevisionPreview from 'components/revision/RevisionPreview';
import RevisionActions from 'components/tables/data/revision/RevisionActions';
import { OnStatusChangeParams } from 'components/tables/data/revision/types';
import H3Medium from 'components/typography/H3Medium';
import P1Medium from 'components/typography/P1Medium';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import ReadonlyText from 'components/typography/ReadonlyText';
import { FEATURE_TOGGLE_REVISION_PLAN_CHANGE_LOG } from 'env';
import panic from 'errors/panic';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { ROLE_ADMIN_ID } from 'model/Role';
import { nanoid } from 'nanoid';
import RevisionArchiveClauseHistory from 'pages/revisions-module/revisions/RevisionArchiveClauseHistory';
import RevisionAttachments from 'pages/revisions-module/revisions/RevisionAttachments';
import RevisionDevices from 'pages/revisions-module/revisions/RevisionDevices';
import RevisionFile from 'pages/revisions-module/revisions/RevisionFile';
import RevisionLogNote from 'pages/revisions-module/revisions/RevisionLogNote';
import RevisionPlanChangeLog from 'pages/revisions-module/revisions/RevisionPlanChangeLog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DASHBOARD_PAGE_PATH, FORMS_PAGE_PATH, REVISIONS_PAGE_PATH } from 'routes/paths';
import {
  ERROR_NOTIFICATION_COLOR,
  INFO_NOTIFICATION_COLOR,
  NO_ENTITY_EXISTS_ERROR_CODE,
  REVISION_NOT_SIGNED_YET_ERROR_CODE,
  REVISION_SIGNATURE_INVALID_ERROR_CODE,
} from 'utils/constants';

type Revision = RevisionGetResponse & {
  deviceSubtype: { deviceTypeId: number; deviceTypeName: string; slug: string }[];
};

/**
 * Page used to display a single revision.
 *
 */
export default function RevisionPage() {
  const departmentText = useDepartmentText();
  const theme = useMantineTheme();
  const documentState = useDocumentVisibility();
  const { getAction, userId, fullName, roleId, hasPermissionAnywhere } = useApi();
  const { revisionId } = useParams();
  const [revision, setRevision] = useState<Revision | null>(null);
  const [refreshKey, setRefreshKey] = useState(nanoid);
  const [userCanViewDevices, setUserCanViewDevices] = useState(roleId === ROLE_ADMIN_ID);
  const [checkingSignature, setCheckingSignature] = useState(false);
  const navigate = useNavigate();
  const { isMobile, isTablet } = useResponsiveDesign();
  const canAccessDocumentList =
    revision && hasPermissionAnywhere(revision.kind === 'revision' ? '*-revisions' : '*-forms');

  const revisionData = useMemo(() => {
    if (!revision || !revision.revisionData) {
      return null;
    }

    return JSON.parse(revision.revisionData);
  }, [revision]);

  /**
   * Handles the status change of the revision.
   */
  const onStatusChange = useCallback(
    ({ status: revisionStatus, reason, fileId, technician }: OnStatusChangeParams) => {
      setRevision((revision) => {
        if (!revision) {
          /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */
          return revision;
        }

        const logs = revision.logs ?? [];
        const maxId = Math.max(...logs.map((log) => log.revisionChangeLogId));

        logs.push({
          revisionId: revision.revisionId,
          revisionChangeLogId: maxId + 1,
          oldStatus: revision.revisionStatus,
          newStatus: revisionStatus,
          timestamp: new Date().toISOString(),
          // first name and last name do not matter, fullName is used instead
          user: { userId: userId!, fullName: fullName!, firstName: '', lastName: '' },
          note: reason ?? undefined,
        });

        setRefreshKey(nanoid());

        return {
          ...revision,
          revisionStatus,
          logs,
          fileId: fileId ?? revision.fileId,
          technician: technician ?? revision.technician,
        };
      });
    },
    [setRevision]
  );

  /**
   * Handles the case when the user no longer has access to the revision.
   * Redirects ACL-only visitors to the dashboard and table visitors to their document list.
   */
  const onNoLongerAccessible = useCallback(() => {
    navigate(
      canAccessDocumentList
        ? revision?.kind === 'revision'
          ? REVISIONS_PAGE_PATH.original
          : FORMS_PAGE_PATH.original
        : DASHBOARD_PAGE_PATH.original
    );
  }, [canAccessDocumentList, navigate, revision?.kind]);

  // Fetch the revision data.
  useEffect(() => {
    if (revisionId) {
      const action = getAction('RevisionGet');

      action({ parameters: { revisionId } })
        .then((response) => {
          const deviceSubtypes = response.devices.map((device) => device.deviceSubtype);
          const deviceSubtypesMap: {
            [deviceTypeId: number]: { deviceTypeName: string; deviceTypeId: number; slug: string };
          } = {};
          deviceSubtypes.forEach((deviceSubtype) => {
            deviceSubtypesMap[deviceSubtype!.deviceTypeId] = deviceSubtype!;
          });

          const uniqDeviceSubtypes = Object.values(deviceSubtypesMap).map((val) => val);

          setRevision({ ...response, deviceSubtype: uniqDeviceSubtypes });
        })
        .catch((e) => {
          if (e?.response?.data?.error?.code === NO_ENTITY_EXISTS_ERROR_CODE) {
            navigate(DASHBOARD_PAGE_PATH.original, { replace: true });
          } else {
            panic(e);
          }
        });
    }
  }, [refreshKey, revisionId]);

  // Fetch permissions for the user.
  useEffect(() => {
    if (roleId === ROLE_ADMIN_ID) {
      setUserCanViewDevices(true);
    } else if (revision) {
      const userHasPermissionInDepartment = getAction('AuthUserHasPermissionInDepartment');

      userHasPermissionInDepartment({
        parameters: { departmentId: String(revision.department.departmentId), permissionSlug: 'browse-devices' },
      })
        .then(({ hasPermission }) => setUserCanViewDevices(hasPermission))
        .catch(panic);
    }
  }, [roleId, revision]);

  // Refresh the revision if it is being signed and the document becomes
  // visible (user returns to the tab).
  useEffect(() => {
    (async () => {
      if (
        revisionId &&
        documentState === 'visible' &&
        revision?.revisionStatus.slug === 'being-signed' &&
        revision.canRefreshSignature === true
      ) {
        try {
          setCheckingSignature(true);

          const getRevision = getAction('RevisionGet');
          const getSignedDoc = getAction('RevisionSaveSignedDoc');

          const { revisionStatus, canRefreshSignature } = await getRevision({ parameters: { revisionId } });

          if (revisionStatus.slug !== 'being-signed' || canRefreshSignature !== true) {
            setRefreshKey(nanoid());
            return; // Reload when signing finishes or refresh permission is lost.
          }

          await getSignedDoc({ parameters: { revisionId } });

          setRefreshKey(nanoid()); // No error = refresh the revision.
        } catch (error: any) {
          const code = error?.response?.data?.error?.code;

          if (code === REVISION_NOT_SIGNED_YET_ERROR_CODE) {
            const message =
              revision.technician!.userId === userId
                ? revision.kind === 'revision'
                  ? 'Využite tlačidlo "Podpísať", aby ste dokončili podpísanie revíznej správy pomocou služby BrainIT.'
                  : 'Využite tlačidlo "Podpísať", aby ste dokončili podpísanie formulára pomocou služby BrainIT.'
                : revision.kind === 'revision'
                ? 'Revízny technik ešte nepodpísal revíznu správu. Skúste to prosím neskôr alebo kontaktujte revízneho technika.'
                : 'Vypracovateľ ešte nepodpísal formulár. Skúste to prosím neskôr alebo kontaktujte vypracovateľa.';

            showNotification({
              color: ERROR_NOTIFICATION_COLOR,
              title: 'Dokument ešte nie je podpísaný',
              message,
            });
          } else if (code === REVISION_SIGNATURE_INVALID_ERROR_CODE) {
            showNotification({
              color: ERROR_NOTIFICATION_COLOR,
              title:
                revision.kind === 'revision' ? 'Podpis revíznej správy je neplatný' : 'Podpis formulára je neplatný',
              message:
                revision.kind === 'revision'
                  ? 'Podpis revíznej správy je neplatný. Skontrolujte platnosť svojho certifikátu alebo kontaktujte administrátora.'
                  : 'Podpis formulára je neplatný. Skontrolujte platnosť svojho certifikátu alebo kontaktujte administrátora.',
            });
          } else {
            panic(error);
          }
        } finally {
          setCheckingSignature(false);
        }
      }
    })();
  }, [documentState, revision?.revisionStatus.slug, revision?.canRefreshSignature, userId]);

  const hasRevisionFile = revision && ((!revision.kepSignable && revision.fileId) || revision.signedFileId);
  const previewEnabled = !hasRevisionFile;
  const isRevisionKind = revision?.kind === 'revision';

  return (
    <DashboardLayout
      title={`Detail ${isRevisionKind ? 'revíznej správy' : 'formulára'}: ${revision?.revisionName ?? ''}`}
      breadcrumbs={[
        {
          title: isRevisionKind ? 'Revízne správy' : 'Formuláre',
          link: canAccessDocumentList
            ? isRevisionKind
              ? REVISIONS_PAGE_PATH.original
              : FORMS_PAGE_PATH.original
            : undefined,
        },
        { title: revision?.revisionName ?? 'Načítavam...' },
      ]}
      actions={
        revision && (
          <RevisionActions
            revision={revision}
            hideView
            compactOnTablet={isMobile}
            onStatusChange={onStatusChange}
            onNoLongerAccessible={onNoLongerAccessible}
          />
        )
      }
    >
      {revision && (
        <Stack spacing={24}>
          <FormInputGroup
            groupTitle="Základné informácie"
            stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
          >
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Názov" value={revision.revisionName} />
              <ReadonlyText
                title="Deadline"
                value={revision.deadline ? new Date(revision.deadline).toLocaleDateString() : '-'}
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Organizácia" value={revision.organization.organizationName} />
              <ReadonlyText
                title={departmentText('Položka úrovne', 'Stredisko')}
                value={revision.department.departmentName}
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Typ" value={revision.deviceType.deviceTypeName} />
              <ReadonlyText
                title="Druh"
                value={revision.deviceSubtype.map((item) => ({ key: item.deviceTypeId, value: item.deviceTypeName }))}
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText title="Zadávateľ" value={revision.assignedBy.organizationName} />
              <ReadonlyText
                title={revision.kind === 'revision' ? 'Revízny technik' : 'Vypracovateľ'}
                value={
                  revision.technician ? (
                    revision.technician.fullName
                  ) : (
                    <Toast size="s" type="fail" message="Nepridelený" withCloseButton={false} />
                  )
                }
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText
                title="Ukončená"
                value={revision.finishedAt ? new Date(revision.finishedAt).toLocaleDateString() : '-'}
              />
              <ReadonlyText
                title="Preplánovanie termínov"
                value={
                  revision.planNextRevision === 'skipped'
                    ? 'Preskočené'
                    : revision.planNextRevision === 'planned'
                    ? 'Vykonané'
                    : '-'
                }
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText
                title="Deadline na prepečiatkovanie"
                value={revision.documentValidUntil ? new Date(revision.documentValidUntil).toLocaleDateString() : '-'}
              />
              <ReadonlyText
                title={
                  revision.kind === 'revision'
                    ? 'Dátum nasledujúcej revíznej správy'
                    : 'Dátum nasledujúceho vypracovania'
                }
                value={revision.nextRevisionDate ? new Date(revision.nextRevisionDate).toLocaleDateString() : '-'}
              />
            </SimpleGrid>
            <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
              <ReadonlyText
                title="Stav"
                value={<RevisionStatusIcon statusId={revision.revisionStatus.revisionStatusId} kind={revision.kind} />}
              />
              <ReadonlyText title="Šablóna" value={revision.revisionTemplate?.name ?? '-'} />
            </SimpleGrid>
          </FormInputGroup>

          {revision.remoteSource && (
            <FormInputGroup
              groupTitle="Externý zdroj"
              stackProps={{ bg: 'white', pb: 40, pr: 40, style: { borderRadius: '4px' } }}
            >
              <SimpleGrid cols={2} spacing={isTablet ? 20 : 40}>
                <ReadonlyText title="Zdroj" value={revision.remoteSource.name} />
                <ReadonlyText title="Externé ID" value={revision.remoteId ?? '-'} />
                <ReadonlyText
                  title="Externé URL"
                  value={
                    revision.remoteUrl ? (
                      <Box>
                        <a rel="external noreferrer" target="_blank" href={revision.remoteUrl}>
                          {revision.remoteUrl}
                        </a>
                      </Box>
                    ) : (
                      '-'
                    )
                  }
                />
              </SimpleGrid>
            </FormInputGroup>
          )}

          {FEATURE_TOGGLE_REVISION_PLAN_CHANGE_LOG && userCanViewDevices && (
            <RevisionPlanChangeLog revision={revision} />
          )}

          {hasRevisionFile && <RevisionFile revision={revision} />}

          {revision.archiveClauseFileId && <RevisionArchiveClauseHistory revisionId={revision.revisionId} />}

          <Stack pt={24} pb={40} px={isTablet ? 20 : 40} bg="white" style={{ borderRadius: '4px' }} spacing={24}>
            <H3Medium color="gray.7">Záznamy</H3Medium>
            {isMobile ? (
              <Stack spacing={0}>
                {(revision.logs || []).map((log) => {
                  return (
                    <Stack
                      key={log.revisionChangeLogId}
                      spacing={12}
                      py={16}
                      px={16}
                      sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[1]}` })}
                    >
                      <ReadonlyText
                        title="Nový stav"
                        value={<RevisionStatusIcon statusId={log.newStatus.revisionStatusId} kind={revision.kind} />}
                      />
                      <ReadonlyText title="Používateľ" value={log.user?.fullName ?? ''} />
                      <ReadonlyText title="Dátum" value={new Date(log.timestamp).toLocaleString()} />
                      <ReadonlyText title="Poznámka" value={<RevisionLogNote note={log.note ?? ''} />} />
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Stack spacing={0}>
                <Box className="grid grid-cols-[25%_15%_20%_1fr] rounded-[4px]" p={16} bg="gray.0">
                  <P1Medium>Nový stav</P1Medium>
                  <P1Medium>Používateľ</P1Medium>
                  <P1Medium>Dátum</P1Medium>
                  <P1Medium>Poznámka</P1Medium>
                </Box>
                {(revision.logs || []).map((log) => {
                  return (
                    <Box
                      py={24}
                      px={16}
                      key={log.revisionChangeLogId}
                      style={{ borderBottom: `1px solid ${theme.colors.gray[1]}`, alignItems: 'flex-start' }}
                      className="grid grid-cols-[25%_15%_20%_1fr]"
                    >
                      <RevisionStatusIcon statusId={log.newStatus.revisionStatusId} kind={revision.kind} />
                      <P2Regular>{log.user?.fullName}</P2Regular>
                      <P2Regular>{new Date(log.timestamp).toLocaleString()}</P2Regular>
                      <P2Regular>
                        <RevisionLogNote note={log.note ?? ''} />
                      </P2Regular>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>

          {revisionData && <RevisionAttachments revision={revision} revisionData={revisionData} />}
          {userCanViewDevices && <RevisionDevices revision={revision} />}

          {revision && revisionData && (roleId === ROLE_ADMIN_ID || previewEnabled) && (
            <Stack spacing={24} py={24} px={isTablet ? 20 : 40} style={{ borderRadius: '4px' }} bg="white">
              <Group spacing={8}>
                <H3Medium color="gray.7">
                  Náhľad {revision.kind === 'revision' ? 'revíznej správy' : 'formulára'}
                </H3Medium>
                {!previewEnabled && roleId === ROLE_ADMIN_ID && (
                  <P1Regular color="gray.5">
                    (Viditeľný iba pre administrátorov v stave {revision.revisionStatus.revisionStatusName})
                  </P1Regular>
                )}
              </Group>
              <Box pos="relative">
                <RevisionPreview height="calc(100vh - 150px)" revisionData={revisionData} revision={revision} />
              </Box>
            </Stack>
          )}
        </Stack>
      )}

      {checkingSignature && (
        <Notification
          color={INFO_NOTIFICATION_COLOR}
          title={
            <Group spacing={8}>
              <Box>Kontrola stavu podpisovania</Box>
              <Loader size={16} />
            </Group>
          }
          style={{ position: 'fixed', bottom: 16, right: 16 }}
          maw={440}
        >
          Objekto momentálne komunikuje so službou BrainIT, aby zistil stav podpisovania{' '}
          {revision?.kind === 'revision' ? 'revíznej správy' : 'formulára'} a overuje platnosť podpisu. Táto kontrola
          trvá približne 15 sekúnd.
        </Notification>
      )}
    </DashboardLayout>
  );
}
